import { loadStudioBaseTranslation } from '@repo/studio-i18n-translations-base/load-translation'
import { loadStudioSharedTranslation } from '@repo/studio-i18n-translations-shared/load-translation'
import type { StudioTranslateFn } from '@repo/studio-config/studio-config-context'
import { SHELL_NAMESPACE_LOADERS } from './shell-namespaces'

type TranslationRecord = Record<string, unknown>

/**
 * Flattens a (possibly deeply nested) translation record into a flat
 * `"studio.x.y.z": "value"` map, matching i18next's split-prefix resolution.
 * Keys in the source JSON may themselves contain dots (e.g. `"studio.home"`)
 * and they contribute their literal dot-prefix to the final key.
 */
export function flattenTranslations(obj: TranslationRecord, prefix = ''): Record<string, string> {
  const result: Record<string, string> = {}
  for (const [key, value] of Object.entries(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key
    if (typeof value === 'string') {
      result[fullKey] = value
    } else if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(result, flattenTranslations(value as TranslationRecord, fullKey))
    }
  }
  return result
}

function loadOne(loader: 'base' | 'shared', locale: string, ns: string) {
  return loader === 'base'
    ? loadStudioBaseTranslation(locale, ns)
    : loadStudioSharedTranslation(locale, ns)
}

/**
 * Returns a flat `Record<string, string>` of all studio chrome strings for the
 * given locale (English fallback when the locale-specific file is missing).
 * Translation JSONs are static package files so this is cheap and safe to call
 * per-request; the CDN caches the full page response via Cache-Control headers.
 */
export async function loadShellTranslations(locale: string): Promise<Record<string, string>> {
  const namespaceMessages = await Promise.all(
    SHELL_NAMESPACE_LOADERS.map(async ({ ns, loader }) => {
      return (await loadOne(loader, locale, ns)) ?? (await loadOne(loader, 'en', ns)) ?? {}
    }),
  )

  // Each namespace JSON is shaped `{ studio: { <ns>: { ... } } }`, so merging the
  // *nested* objects with `Object.assign` would clobber the shared `studio` key
  // and drop every namespace but the last. Instead flatten each namespace on its
  // own — the resulting keys (`studio.common.*`, `studio.flow.*`, …) never
  // collide, so combining the flat maps is lossless.
  const flattened: Record<string, string> = {}
  for (const msgs of namespaceMessages) {
    Object.assign(flattened, flattenTranslations(msgs))
  }

  return flattened
}

/**
 * Backs `StudioConfig.t` with a flat map loaded by `loadShellTranslations`, for
 * hosts that have no react-i18next instance (the Next.js apps). Interpolation
 * matches i18next's `{{name}}` placeholders so shared chrome strings render the
 * same under either host.
 */
export function createFlatStudioTranslate(flatMap: Record<string, string>): StudioTranslateFn {
  return (key, options) => {
    let value = flatMap[key] ?? key
    if (options) {
      for (const [name, replacement] of Object.entries(options)) {
        value = value.replace(new RegExp(`\\{\\{${name}\\}\\}`, 'g'), String(replacement))
      }
    }
    return value
  }
}
